import { useMemo } from 'react';
import { useFormContext, Controller, FieldValues } from 'react-hook-form';

import {
  HTJ_STRING_PATTERN,
  PHONE_PATTERN,
  ValidationWarningMessage,
} from '../../constants';
import type { FormState } from '../../SpaceRenovationRequestCreateForm.type';
import type { ExecutorProps } from '../RenovationTypes.type';

import Stack from '@mui/material/Stack';
import DatePicker from 'components/controls/date-picker';
import ControlLabel from 'shared/components/ControlLabel';
import Input from 'shared/components/Input';
import ValidationWarning from 'components/validation-warning';
import { RenovationExecutorFieldContainer } from '../RenovationTypes.style';

const currentYear = new Date().getFullYear();

const RenovationExecutorPerson = ({ idx }: ExecutorProps) => {
  const {
    clearErrors,
    control,
    formState: { errors },
    register,
  } = useFormContext<FormState>();

  const baseName = useMemo(() => `renovationTypes.${idx}` as const, []);

  const validateFullNameMaxLength = (
    value: string | undefined,
    formValues: FieldValues,
    index: number
  ) => {
    const first = formValues.renovationTypes[index]?.personFirstName ?? '';
    const last = formValues.renovationTypes[index]?.personLastName ?? '';
    return (
      first.length + last.length < 100 ||
      'Nimikenttien yhteenlaskettu merkkimäärä ylittää sallitun 100 merkkiä. Lyhennä etu- tai sukunimeä.'
    );
  };

  const validatePattern = (
    value: string | undefined,
    pattern: RegExp,
    message: string
  ) => {
    if (value !== undefined && !pattern.test(value)) return message;

    return true;
  };

  return (
    <Stack spacing={2} sx={{ width: { xs: '100%', md: '80%' } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <RenovationExecutorFieldContainer>
          <ControlLabel required>Etunimi</ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.personFirstName?.message}
            handleInitErrors={clearErrors}
            Component={
              <Input
                {...register(`${baseName}.personFirstName`, {
                  required: ValidationWarningMessage.Required,
                  validate: {
                    htjString: (value) =>
                      validatePattern(
                        value,
                        HTJ_STRING_PATTERN,
                        ValidationWarningMessage.HTJInvalid
                      ),
                    maxLength: (value, formValues) =>
                      validateFullNameMaxLength(value, formValues, idx),
                  },
                })}
                variant="underline"
              />
            }
          />
        </RenovationExecutorFieldContainer>

        <RenovationExecutorFieldContainer>
          <ControlLabel required>Sukunimi</ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.personLastName?.message}
            handleInitErrors={clearErrors}
            Component={
              <Input
                {...register(`${baseName}.personLastName`, {
                  required: ValidationWarningMessage.Required,
                  validate: {
                    htjString: (value) =>
                      validatePattern(
                        value,
                        HTJ_STRING_PATTERN,
                        ValidationWarningMessage.HTJInvalid
                      ),
                    maxLength: (value, formValues) =>
                      validateFullNameMaxLength(value, formValues, idx),
                  },
                })}
                variant="underline"
              />
            }
          />
        </RenovationExecutorFieldContainer>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <RenovationExecutorFieldContainer>
          <ControlLabel required>Syntymäaika</ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.personBirthday?.message}
            handleInitErrors={clearErrors}
            Component={
              <Controller
                control={control}
                name={`${baseName}.personBirthday`}
                rules={{
                  required: ValidationWarningMessage.Required,
                }}
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    date={value ?? null}
                    endIn={currentYear}
                    handleChange={onChange}
                    placeholder="DD.M.YYYY"
                    variant="underline"
                  />
                )}
              />
            }
          />
        </RenovationExecutorFieldContainer>

        <RenovationExecutorFieldContainer>
          <ControlLabel required>Sähköpostiosoite</ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.personEmail?.message}
            handleInitErrors={clearErrors}
            Component={
              <Input
                {...register(`${baseName}.personEmail`, {
                  required: ValidationWarningMessage.Required,
                })}
                placeholder="mail@mail.com"
                variant="underline"
              />
            }
          />
        </RenovationExecutorFieldContainer>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <RenovationExecutorFieldContainer>
          <ControlLabel required>Matkapuhelinnumero</ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.personPhone?.message}
            handleInitErrors={clearErrors}
            Component={
              <Input
                {...register(`${baseName}.personPhone`, {
                  required: ValidationWarningMessage.RequiredInternationalPhone,
                  pattern: {
                    value: PHONE_PATTERN,
                    message: ValidationWarningMessage.InvalidInternationalPhone,
                  },
                })}
                placeholder="+35850123456"
                variant="underline"
              />
            }
          />
        </RenovationExecutorFieldContainer>
      </Stack>
    </Stack>
  );
};

export default RenovationExecutorPerson;
